import React from 'react';
import { Box, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ApiIcon from '@mui/icons-material/Api';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { useTranslation } from 'react-i18next';

const NewsPipelineAnimation = () => {
    const { t } = useTranslation();
    const transitionConfig = { duration: 10, repeat: Infinity, ease: "linear" };

    return (
        <Box sx={{ width: '100%', height: '100%', minHeight: 280, position: 'relative', backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: 2, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
            <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1 }}>
                <line x1="15%" y1="45%" x2="50%" y2="45%" stroke="rgba(255,255,255,0.15)" strokeWidth="2" strokeDasharray="4,4" />
                <line x1="50%" y1="45%" x2="85%" y2="45%" stroke="rgba(255,255,255,0.15)" strokeWidth="2" strokeDasharray="4,4" />

                {/* Dot 1: Freelancer -> API */}
                <motion.circle cy="45%" r="5" fill="#50728C"
                    animate={{ cx: ['15%', '15%', '15%', '50%', '50%', '50%'], opacity: [0, 0, 1, 1, 0, 0], filter: ['none', 'none', 'drop-shadow(0 0 8px #50728C)', 'drop-shadow(0 0 8px #50728C)', 'none', 'none'] }}
                    transition={{ ...transitionConfig, times: [0, 0.09, 0.1, 0.25, 0.26, 1] }}
                />
                {/* Dot 2: API -> Newsroom */}
                <motion.circle cy="45%" r="5" fill="#f44336" // Red for Fake News
                    animate={{ cx: ['50%', '50%', '50%', '85%', '85%', '85%'], opacity: [0, 0, 1, 1, 0, 0], filter: ['none', 'none', 'drop-shadow(0 0 8px #f44336)', 'drop-shadow(0 0 8px #f44336)', 'none', 'none'] }}
                    transition={{ ...transitionConfig, times: [0, 0.59, 0.6, 0.75, 0.76, 1] }}
                />
            </svg>

            <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 2 }}>
                {/* Node 1: Social/Freelancer */}
                <Box sx={{ position: 'absolute', left: '15%', top: '45%', transform: 'translate(-50%, -50%)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <motion.div animate={{ scale: [1, 1, 1.1, 1, 1, 1] }} transition={{ ...transitionConfig, times: [0, 0.04, 0.05, 0.15, 0.95, 1] }}
                        style={{ backgroundColor: '#171F29', border: '2px solid rgba(255,255,255,0.3)', borderRadius: '50%', padding: '12px', display: 'flex' }}>
                        <CloudUploadIcon sx={{ color: 'white', fontSize: 32 }} />
                    </motion.div>
                    <Box sx={{ position: 'absolute', top: '100%', mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', width: 'max-content' }}>
                        <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold' }}>{t('industryApplications.animations.news.node1Title')}</Typography>
                        <motion.div animate={{ opacity: [0, 0, 1, 1, 0, 0] }} transition={{ ...transitionConfig, times: [0, 0.04, 0.05, 0.15, 0.2, 1] }}>
                            <Typography variant="caption" sx={{ color: '#50728C', fontSize: '0.7rem' }}>{t('industryApplications.animations.news.node1Action')}</Typography>
                        </motion.div>
                    </Box>
                </Box>

                {/* Node 2: Vetra AI API */}
                <Box sx={{ position: 'absolute', left: '50%', top: '45%', transform: 'translate(-50%, -50%)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <motion.div animate={{ borderColor: ['rgba(255,255,255,0.3)', 'rgba(255,255,255,0.3)', 'rgba(80,114,140,0.8)', 'rgba(80,114,140,0.8)', 'rgba(244,67,54,0.8)', 'rgba(244,67,54,0.8)', 'rgba(255,255,255,0.3)'], boxShadow: ['none', 'none', '0 0 20px rgba(80,114,140,0.4)', '0 0 20px rgba(80,114,140,0.4)', '0 0 25px rgba(244,67,54,0.5)', '0 0 25px rgba(244,67,54,0.5)', 'none'] }}
                        transition={{ ...transitionConfig, times: [0, 0.24, 0.25, 0.44, 0.45, 0.55, 1] }}
                        style={{ backgroundColor: '#171F29', border: '2px solid rgba(255,255,255,0.3)', borderRadius: '12px', padding: '12px', display: 'flex' }}>
                        <ApiIcon sx={{ color: 'white', fontSize: 32 }} />
                    </motion.div>
                    <Box sx={{ position: 'absolute', top: '100%', mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', width: 'max-content' }}>
                        <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold' }}>{t('industryApplications.animations.news.node2Title')}</Typography>
                        <Box sx={{ position: 'relative', height: 20, width: '100%', mt: 0.2, display: 'flex', justifyContent: 'center' }}>
                            <motion.div
                            style={{
                                position: 'absolute',
                                textAlign: 'center',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: 160,             // important for wrapping; adjust as needed
                                pointerEvents: 'none',  // so the overlay doesn't block clicks
                            }}
                            animate={{ opacity: [0, 0, 1, 1, 0, 0] }}
                            transition={{ ...transitionConfig, times: [0, 0.24, 0.25, 0.44, 0.45, 1] }}
                            >
                            <Typography
                                variant="caption"
                                sx={{
                                color: '#50728C',
                                fontSize: '0.7rem',

                                // 2-line clamp
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',

                                // nicer wrapping & consistent layout
                                overflowWrap: 'anywhere',     // or 'break-word'
                                wordBreak: 'break-word',
                                textAlign: 'center',
                                lineHeight: 1.2,
                                maxHeight: 'calc(1.2em * 2)', // prevents height “pop” during animation
                                }}
                            >
                                {t('industryApplications.animations.news.node2Action')}
                            </Typography>
                            </motion.div>

                            <motion.div
                            style={{
                                position: 'absolute',
                                textAlign: 'center',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: 160,                 // important for wrapping; tweak as needed
                            }}
                            animate={{ opacity: [0, 0, 1, 1, 0, 0] }}
                            transition={{ ...transitionConfig, times: [0, 0.44, 0.5, 0.6, 0.8, 1] }}
                            >
                            <Typography
                                variant="caption"
                                sx={{
                                color: '#f44336',
                                fontSize: '0.75rem',
                                fontWeight: 'bold',

                                // 2-line clamp
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',

                                // optional niceties
                                overflowWrap: 'anywhere',
                                textAlign: 'center',
                                }}
                            >
                                {t('industryApplications.animations.news.node2Result')}
                            </Typography>
                            </motion.div>
                        </Box>
                    </Box>
                </Box>

                {/* Node 3: Newsroom CMS */}
                <Box sx={{ position: 'absolute', left: '85%', top: '45%', transform: 'translate(-50%, -50%)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <motion.div animate={{ scale: [1, 1, 1.1, 1.1, 1], borderColor: ['rgba(255,255,255,0.3)', 'rgba(255,255,255,0.3)', 'rgba(244,67,54,0.8)', 'rgba(244,67,54,0.8)', 'rgba(255,255,255,0.3)'], boxShadow: ['none', 'none', '0 0 25px rgba(244,67,54,0.6)', '0 0 25px rgba(244,67,54,0.6)', 'none'], color: ['#fff', '#fff', '#f44336', '#f44336', '#fff'] }}
                        transition={{ ...transitionConfig, times: [0, 0.74, 0.75, 0.95, 1] }}
                        style={{ backgroundColor: '#171F29', border: '2px solid rgba(255,255,255,0.3)', borderRadius: '50%', padding: '12px', display: 'flex' }}>
                        <DashboardIcon sx={{ fontSize: 32 }} color="inherit" />
                    </motion.div>
                    <Box sx={{ position: 'absolute', top: '100%', mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', width: 'max-content' }}>
                        <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold' }}>{t('industryApplications.animations.news.node3Title')}</Typography>
                        <motion.div animate={{ opacity: [0, 0, 1, 1, 0, 0] }} transition={{ ...transitionConfig, times: [0, 0.74, 0.75, 0.95, 0.96, 1] }}>
                            <Typography variant="caption" sx={{ color: '#f44336', fontSize: '0.75rem', fontWeight: 'bold' }}>{t('industryApplications.animations.news.node3Result')}</Typography>
                        </motion.div>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default NewsPipelineAnimation;